import React from 'react';
import { useIntl } from 'react-intl';
import type { RolloutDayKey } from 'fym-shared/src/music24/releaseRollout';
import { TypedMemo } from '../../../../../common/types';
import { ConfirmDialog } from '../../../../../common/views/modals/confirmDialog/ConfirmDialog';
import { platformName } from '../../../platformMeta';
import { isSocialPlatform } from '../../../types';
import { planStepTitle } from '../rolloutDayCopy';
import { useRollout } from './RolloutContext';
import type { PlanStepView } from './rolloutModel';
import { sheetMessages } from './sheetMessages';

interface SkipStepDialogProps {
  step: PlanStepView;
  isOpen: boolean;
  onClose: () => void;
  /** The step is skipped: the sheet closes. */
  onSkipped: () => void;
}

/**
 * "Skip this step" (design rollout-plan c2, owner decision 9): what changes (the scheduled posts
 * are canceled and come back to this month, the drafts are deleted), and that the step hides and
 * is not a gap. A posted channel is never undone.
 */
const SkipStepDialogComponent: React.FC<SkipStepDialogProps> = ({ step, isOpen, onClose, onSkipped }) => {
  const intl = useIntl();
  const { actions, plan } = useRollout();
  const posted = step.channels.filter((channel) => channel.state === 'posted' || channel.state === 'publishing');
  const scheduled = step.channels.filter((channel) => channel.state === 'scheduled' || channel.state === 'needsYou');
  const drafts = step.channels.filter((channel) => channel.state === 'draft' || channel.state === 'failed');
  const name = planStepTitle(intl, plan, step.day);
  const feedback = actions.feedback.skip ?? null;

  const title =
    posted.length > 0
      ? intl.formatMessage(sheetMessages.skipPostedTitle, {
          names: posted
            .map((channel) => (isSocialPlatform(channel.platform) ? platformName(channel.platform) : ''))
            .join(', '),
          count: posted.length,
        })
      : intl.formatMessage(sheetMessages.skipTitle, { name });

  const confirm = () => {
    actions
      .skip('skip', step.key as RolloutDayKey)
      .then((ok) => (ok ? onSkipped() : undefined))
      .catch(console.error);
  };

  return (
    <ConfirmDialog
      isOpen={isOpen}
      tone="neutral"
      title={title}
      description={[
        intl.formatMessage(sheetMessages.skipCanceled, { count: scheduled.length }),
        intl.formatMessage(sheetMessages.skipDeleted, { count: drafts.length }),
        intl.formatMessage(sheetMessages.skipHidden),
      ]
        .filter((line) => line !== '')
        .join(' ')}
      confirmLabel={intl.formatMessage(sheetMessages.skipConfirm)}
      cancelLabel={intl.formatMessage(sheetMessages.skipKeep)}
      isBusy={actions.busy === 'skip'}
      error={feedback?.tone === 'danger' ? feedback.message : undefined}
      onConfirm={confirm}
      onCancel={onClose}
    />
  );
};

export const SkipStepDialog = TypedMemo(SkipStepDialogComponent);
